import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Target,
  Tag,
  Workflow,
  ShieldCheck,
  Bug,
  Wrench,
  Activity,
  ClipboardList,
  Sparkles,
  Database,
  Send,
  History,
  PlayCircle,
  XCircle,
  Loader2,
  CircleDot,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import {
  runLeadsSelfTest,
  type SelfTestCheck,
} from "@/lib/admin/leads-selftest.functions";

export const Route = createFileRoute("/admin/plano-leads-rd-crm")({
  head: () => ({
    meta: [
      { title: "Admin — Projeto LEADS RD/CRM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/plano-leads-rd-crm">
      <LeadsRdCrmPage />
    </AdminGate>
  ),
});

type Tone = "default" | "success" | "warning" | "danger" | "info";

function toneClasses(tone: Tone) {
  switch (tone) {
    case "success":
      return { border: "border-emerald-400/40", bg: "from-emerald-500/10 to-transparent", text: "text-emerald-300", chip: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" };
    case "warning":
      return { border: "border-amber-400/40", bg: "from-amber-500/10 to-transparent", text: "text-admin-accent", chip: "bg-amber-500/15 text-amber-200 border-amber-400/30" };
    case "danger":
      return { border: "border-rose-400/40", bg: "from-rose-500/10 to-transparent", text: "text-rose-300", chip: "bg-rose-500/15 text-rose-200 border-rose-400/30" };
    case "info":
      return { border: "border-sky-400/40", bg: "from-sky-500/10 to-transparent", text: "text-sky-300", chip: "bg-sky-500/15 text-sky-200 border-sky-400/30" };
    default:
      return { border: "border-white/10", bg: "from-white/5 to-transparent", text: "text-white", chip: "bg-white/10 text-white/80 border-white/15" };
  }
}

function Card({
  icon: Icon,
  title,
  kicker,
  tone = "default",
  children,
}: {
  icon: React.ElementType;
  title: string;
  kicker?: string;
  tone?: Tone;
  children: React.ReactNode;
}) {
  const t = toneClasses(tone);
  return (
    <section className={`relative overflow-hidden rounded-2xl border ${t.border} bg-gradient-to-br ${t.bg} bg-white/[0.02] p-6 sm:p-8`}>
      <div className="flex items-start gap-4 mb-5">
        <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl border ${t.border} bg-white/5 shrink-0`}>
          <Icon className={`h-5 w-5 ${t.text}`} />
        </div>
        <div className="min-w-0">
          {kicker && (
            <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${t.text}`}>{kicker}</div>
          )}
          <h2 className="mt-0.5 text-xl sm:text-2xl font-bold text-white leading-tight">{title}</h2>
        </div>
      </div>
      <div className="space-y-3 text-sm sm:text-[15px] text-white/75 leading-relaxed">{children}</div>
    </section>
  );
}

function Chip({ children, tone = "default" }: { children: React.ReactNode; tone?: Tone }) {
  const t = toneClasses(tone);
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${t.chip}`}>
      {children}
    </span>
  );
}

function Bullet({ children, tone = "default" }: { children: React.ReactNode; tone?: Tone }) {
  const t = toneClasses(tone);
  return (
    <li className="flex items-start gap-2.5">
      <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${t.text}`} />
      <span>{children}</span>
    </li>
  );
}

type TimelineItem = {
  version: string;
  date: string;
  title: string;
  attempt: string;
  problem?: string;
  outcome: "iterado" | "resolvido" | "parcial";
};

const TIMELINE: TimelineItem[] = [
  {
    version: "v0",
    date: "Início",
    title: "Capturas espalhadas em cada LP",
    attempt:
      "Cada LP (aula experimental, ementa, pré-checkout, pop-up) tinha seu próprio formulário e seu próprio handler chamando a API do RD Marketing diretamente do componente.",
    problem:
      "Validação inconsistente, leads sem telefone/nome, payloads divergentes, e nenhum log centralizado quando algo falhava.",
    outcome: "iterado",
  },
  {
    version: "v1",
    date: "Padronização de tags",
    title: "Taxonomia única de tags e roteamento de funil",
    attempt:
      "Centralizamos o desenho de tags (aulas-exp-*, ementa-site-*, pre-checkout-*, pre-checkout-gg-* para Google Ads, quizz-dor, quizz-pos + lead-quente/morno/frio/parcial) e o roteamento por estágio: estudante → UNIVERSITÁRIOS, morno/quente → FUNIL-POS, frio não-estudante → fica só no Marketing.",
    problem:
      "Resolveu a duplicação de tags, mas os negócios no CRM ainda chegavam sem perguntas/respostas.",
    outcome: "iterado",
  },
  {
    version: "v2",
    date: "Validação rígida (Zod)",
    title: "Schema compartilhado + leads-parciais",
    attempt:
      "Criamos src/lib/leads/validation.ts com Zod e aplicamos no cliente e no servidor. Pop-ups e quizzes passaram a só liberar o conteúdo após captura completa. Lead que abandona meio do funil entra como lead-parcial.",
    problem:
      "Apareceram leads sem nome na recuperação porque o RD Station aceitava o contato sem nome pela API legada — corrigido tornando todos os campos obrigatórios também no servidor.",
    outcome: "iterado",
  },
  {
    version: "v3",
    date: "Primeira integração de Meta",
    title: "Meta Pixel client-side",
    attempt:
      "Pixel disparado no cliente em cada submit + page view. Eventos PageView e Lead com dedup básico.",
    problem:
      "Meta Ads marcava centenas de conversões enquanto a base do CRM mostrava quase nada — campanhas queimando orçamento sem retorno mensurável.",
    outcome: "iterado",
  },
  {
    version: "v4",
    date: "Meta Conversions API + auditoria",
    title: "CAPI server-side + tabela lead_capture_attempts",
    attempt:
      "Subimos a Conversions API server-side com event_id para deduplicar com o Pixel. Criamos a tabela lead_capture_attempts registrando payload, endpoint, status e origem de cada tentativa, mais o card LeadFunnelCard no /admin para auditar o funil em tempo real.",
    problem:
      "Conseguimos ver a discrepância exata: leads chegavam no RD Marketing, mas o CRM recebia o negócio sem as anotações de qualificação.",
    outcome: "iterado",
  },
  {
    version: "v5",
    date: "Unificação de pipeline",
    title: "Pipeline server-side único (postprocess.server.ts)",
    attempt:
      "Todas as fontes — aula exp, ementa, pré-checkout, quizz, pop-up — passaram a chamar src/lib/leads/postprocess.server.ts. Cookies _fbc/_fbp e UTMs capturados no cliente e reenviados pelo servidor. Uma correção agora se propaga para todas as LPs.",
    problem:
      "Restava o bug crítico: anotações ainda intermitentes no CRM dependendo do formato de campo custom do RD.",
    outcome: "iterado",
  },
  {
    version: "v6",
    date: "Testes automatizados de fluxo",
    title: "Endpoints test-leads e test-aulaexp",
    attempt:
      "Criamos endpoints protegidos por secret para disparar leads sintéticos cobrindo os 3 roteamentos (UNIVERSITÁRIOS, FUNIL-POS morno, FUNIL-POS quente) em todas as 5 áreas de aula experimental.",
    problem:
      "Os testes confirmaram que o RD Marketing recebia 100% dos leads, mas o CRM falhava em ~30% das criações de negócio quando o campo custom vinha em um formato que a API recusava — e o builder antigo não tinha fallback, ele apenas registrava o erro e seguia em frente.",
    outcome: "iterado",
  },
  {
    version: "v7",
    date: "Pipeline unificado estável",
    title: "CRM builder resiliente em cascata + mapeamento travado",
    attempt:
      "Reescrevemos o builder de negócio do RD CRM para tentar os formatos aceitos pela API em cascata (objeto, array de objetos, valor direto) e sempre anexar perguntas e respostas como anotação estruturada — mesmo quando o campo custom falha. Mapeamento dos campos customizados foi revalidado e travado em código, fim do passo manual por LP. Roteamento por estágio reaplicado de forma consistente em todas as fontes.",
    outcome: "resolvido",
  },
  {
    version: "v8",
    date: "Ajustes pós-go-live da campanha Meta",
    title: "URL longa do Meta Ads + leads parciais sem placeholder",
    attempt:
      "Dois sintomas em produção: (1) leads da campanha Meta entravam com status validation_fail porque page_url passava de 400 chars (fbclid + tracker_trk + utm_*); (2) o painel mostrava placeholders 'Contato capturado antes das perguntas' que persistiam mesmo após o submit final. Correções: criamos trackingString(max) em src/lib/leads/validation.ts que TRUNCA em vez de rejeitar (URLs/fbc/fbp → 2000 chars, UTMs → 400) e aplicamos em todos os schemas de captura (aula, quizz, ementa, pré-checkout). Leads parciais agora entram com respostas:[] e o submit final sobrescreve via partial_lead_id no DB e na anotação do RD CRM. Badge 'parcial' no /admin/leads no lugar do placeholder.",
    outcome: "resolvido",
  },
  {
    version: "v9 (HOJE · CONCLUÍDO)",
    date: "Validação em produção",
    title: "Leads reais entrando completos no RD Marketing e no CRM",
    attempt:
      "Após o deploy dos ajustes da v8, a próxima leva de leads orgânicos e da campanha Meta entrou com status ok, sem falhas de validation_fail por URL longa, e os leads parciais que finalizaram o quiz apareceram no RD CRM com perguntas e respostas reais nas anotações. Pipeline declarado estável; segue em acompanhamento via /admin/leads nos próximos dias para confirmar consistência sob volume.",
    outcome: "resolvido",
  },
  {
    version: "v10 (CONCLUÍDO)",
    date: "Hardening de segurança pós-validação",
    title: "Remoção do endpoint temporário de teste",
    attempt:
      "Com o pipeline já validado em produção (v9), o endpoint POST /api/public/hooks/test-aulaexp — usado durante os testes do RD/CRM para injetar 15 leads sintéticos — foi removido por completo (src/routes/api/public/hooks/test-aulaexp.ts deletado). Ele tinha um ONE_SHOT_TOKEN hardcoded como fallback do CRON_SECRET e aceitava o segredo via query string (?secret=), o que vazava o token em logs de CDN/proxy. Como o fluxo real de captura já estava confirmado, manter um endpoint público capaz de poluir a tabela leads / RD Station / CRM era risco desnecessário. Nenhum código de captura, tracking, Meta CAPI, RD ou CRM foi tocado.",
    outcome: "resolvido",
  },
  {
    version: "v11 (CONCLUÍDO)",
    date: "Deduplicação Meta + catálogo vivo",
    title: "event_id compartilhado (Pixel + CAPI + RD) e documentação dinâmica no /admin/leads",
    attempt:
      "Duas frentes concluídas nesta etapa. (1) Deduplicação de leads no Meta Ads: o pipeline server-side agora gera um metaEventId (UUID) por captura e o repassa como campo customizado cf_meta_event_id (+ cf_meta_fbc / cf_meta_fbp) para o RD Station Marketing, além do próprio Meta Pixel (client) e da Meta CAPI (server) — os três canais compartilham o mesmo event_id, permitindo que o Gerenciador de Anúncios deduplique o terceiro disparo do RD e resolva a triplicação histórica. Falta apenas a configuração manual no RD (criar os 3 campos customizados e mapear cf_meta_event_id como event_id na integração RD → Meta). (2) Documentação viva no painel /admin/leads: criado src/lib/leads/capture-flow-catalog.ts como fonte de verdade única. Todas as tabelas de referência (fluxos, LPs, mapeamento por pós, tags RD, roteamento CRM, dedup Meta, etapas do funil, coluna Compras e checklist para devs) são renderizadas dinamicamente a partir desse catálogo — adicionar uma nova captura passa a ser 1 entrada no catálogo, sem manutenção paralela. Aplicada também a nomenclatura oficial dos fluxos (LEAD - Aulas Experimentais / Ementa (site) / Pré-checkout (site) / Quizz Dor / Quizz Pós / Revista / AprovaFEO), ordem canônica das pós (POS-TODAS → ORTO → DOR → ESPORTE → GERONTO → TRAUMA → PRESCRICAO) e apresentação do roteamento CRM linha a linha.",
    outcome: "resolvido",
  },
  {
    version: "v12 (CONCLUÍDO)",
    date: "Visibilidade do time comercial no painel",
    title: "Coluna Vendedora + reordenação + nome padronizado da negociação + tags de compra legíveis",
    attempt:
      "Quatro melhorias no /admin/leads focadas em dar visão end-to-end para marketing e comercial. (1) Reordenação da tabela: coluna Compras movida para depois de RD CRM (antes de Ações), agrupando os sinais comerciais no fim da linha. (2) Nova coluna Vendedora entre CRM e Compras, mostrando qual vendedora do time comercial recebeu o deal (Bruna / Weruska / Ingrid) — computada client-side a partir de src/lib/leads/sellers.ts, fonte compartilhada com src/lib/aulas-experimentais/rdcrm.server.ts, então o painel reproduz exatamente o round-robin determinístico do servidor sem precisar persistir o dono no banco. (3) Nome padronizado da negociação criada no RD CRM: passou de \"{Nome} — {Pós} [classificacao]\" para o padrão {FLUXO} - POS-{ÁREA} - {TEMPERATURA} — ex.: AULAS-EXP - POS-ORTO - QUENTE, EMENTA - POS-DOR - MORNO, PRE-CHK - POS-ESPORTE - QUENTE. O comercial identifica fonte + pós + temperatura já no título do deal, sem abrir a anotação. (4) Coluna Compras reformatada: cada badge agora exibe PRODUTO - FONTE (ex.: POS-ORTO - CRM, Star Cursos - MKT, Agente IA - MKT), com rótulos amigáveis para todos os produtos do catálogo (pós, Star, certificações, Agente IA, e-books, imersões, provas, CBFEO, FisioEmp). A documentação dinâmica no /admin/leads (seções 5, 8 e nova 8b) foi atualizada em sincronia com essas mudanças.",
    outcome: "resolvido",
  },
  {
    version: "v13 (CONCLUÍDO)",
    date: "Migração de tracking Meta para o GTM",
    title: "Site deixa de disparar Meta Pixel/CAPI — GTM assume 100%",
    attempt:
      "Consolidação de todo o tracking do Meta Ads no Google Tag Manager. O site parou de carregar o Meta Pixel base (removido do __root.tsx), parou de chamar a Meta Conversions API server-side (src/lib/leads/meta-capi.server.ts deletado) e parou de enviar cf_meta_event_id / cf_meta_fbc / cf_meta_fbp para o RD Station Marketing. A leitura de cookies _fbc/_fbp e a geração de event_id também foram removidas de client-tracking.ts, dos schemas Zod de todas as capturas (aulas-exp, ementa, pré-checkout, quizz-dor, quizz-pos, revista, aprovafeo) e dos componentes de formulário. Em contrapartida, o cliente passa a empurrar dois eventos limpos no window.dataLayer: feo_lead_saved (parcial) e feo_lead_qualified (final), com lead_source, lead_area, lead_product e lead_classificacao — o GTM escuta esses dois eventos e cuida sozinho de Pixel, CAPI, dedup por event_id, hashing SHA-256 e leitura de cookies. RD Marketing e RD CRM continuam recebendo os leads exatamente como antes; a coluna meta_capi_status em lead_capture_attempts continua existindo por compatibilidade mas fica sempre null. Documentação completa em /admin/plano-tracking-gtm.",
    outcome: "resolvido",
  },
];


function TimelineEntry({ item, last }: { item: TimelineItem; last: boolean }) {
  const tone: Tone = item.outcome === "resolvido" ? "success" : item.outcome === "parcial" ? "warning" : "info";
  const t = toneClasses(tone);
  return (
    <li className="relative pl-10 pb-6">
      {!last && <span className="absolute left-[15px] top-7 bottom-0 w-px bg-white/15" />}
      <span className={`absolute left-0 top-1 inline-flex h-8 w-8 items-center justify-center rounded-full border ${t.border} bg-[#0b0a1f]`}>
        {item.outcome === "resolvido" ? (
          <CheckCircle2 className={`h-4 w-4 ${t.text}`} />
        ) : (
          <CircleDot className={`h-4 w-4 ${t.text}`} />
        )}
      </span>
      <div className={`rounded-xl border ${t.border} bg-white/[0.03] p-4`}>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <Chip tone={tone}>{item.version}</Chip>
          <span className="text-[11px] uppercase tracking-wider text-white/50">{item.date}</span>
        </div>
        <div className="text-white font-semibold">{item.title}</div>
        <p className="mt-2 text-sm text-white/75 leading-relaxed">
          <strong className="text-white/90">Tentativa:</strong> {item.attempt}
        </p>
        {item.problem && (
          <p className="mt-2 text-sm text-amber-200/80 leading-relaxed">
            <strong className="text-amber-200">Erro identificado:</strong> {item.problem}
          </p>
        )}
      </div>
    </li>
  );
}

function SelfTestRunner() {
  const run = useServerFn(runLeadsSelfTest);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    checks: SelfTestCheck[];
    summary: { pass: number; warn: number; fail: number };
    ranAt: string;
  } | null>(null);

  async function handleRun() {
    setLoading(true);
    setError(null);
    try {
      const r = await run();
      setResult(r as any);
    } catch (e: any) {
      setError(e?.message ?? "Falha ao executar o teste");
    } finally {
      setLoading(false);
    }
  }

  const groups = result
    ? Array.from(new Set(result.checks.map((c) => c.group)))
    : [];

  return (
    <Card icon={PlayCircle} title="Teste de integração ao vivo" kicker="Validação final" tone="success">
      <p>
        Executa, no servidor, uma bateria de verificações sem criar nenhum lead real: confere os
        secrets das integrações, valida o schema de captura com casos positivos e negativos, gera
        as tags para cenários típicos e confirma que o log de auditoria está acessível.
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <button
          onClick={handleRun}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500/90 hover:bg-emerald-400 disabled:opacity-60 text-[#0b0a1f] px-4 py-2 text-sm font-semibold transition"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
          {loading ? "Executando..." : result ? "Rodar novamente" : "Rodar teste agora"}
        </button>
        {result && (
          <div className="flex gap-2 flex-wrap text-[11px]">
            <Chip tone="success">{result.summary.pass} OK</Chip>
            {result.summary.warn > 0 && <Chip tone="warning">{result.summary.warn} aviso(s)</Chip>}
            {result.summary.fail > 0 && <Chip tone="danger">{result.summary.fail} falha(s)</Chip>}
            <span className="text-white/40 self-center">
              Executado em {new Date(result.ranAt).toLocaleString("pt-BR")}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-rose-400/40 bg-rose-500/10 p-3 text-rose-200 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-4">
          {groups.map((g) => (
            <div key={g} className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-white/60 border-b border-white/10 bg-white/[0.03]">
                {g}
              </div>
              <ul className="divide-y divide-white/5">
                {result.checks
                  .filter((c) => c.group === g)
                  .map((c) => (
                    <li key={c.id} className="px-4 py-2.5 flex items-start gap-3 text-sm">
                      {c.status === "pass" ? (
                        <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                      ) : c.status === "warn" ? (
                        <AlertTriangle className="h-4 w-4 mt-0.5 text-admin-accent shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 mt-0.5 text-rose-400 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-white">{c.label}</div>
                        <div className="text-white/50 text-xs mt-0.5 break-words">{c.detail}</div>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function LeadsRdCrmPage() {
  return (
    <div className="min-h-screen">
      <div className="px-4 py-10 sm:py-14">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 gap-3">
            <Link to="/admin/planos" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition">
              <ArrowLeft className="h-4 w-4" /> Projetos
            </Link>
            <Chip tone="success">
              <CheckCircle2 className="h-3.5 w-3.5" /> Projeto concluído
            </Chip>
          </div>

          {/* Hero */}
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/10 via-white/[0.03] to-transparent p-7 sm:p-10 mb-8">
            <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300 font-semibold mb-3">Projeto · Captura & CRM</div>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-[1.05]">
              LEADS RD / CRM
            </h1>
            <p className="mt-4 text-white/70 text-base sm:text-lg max-w-3xl">
              Histórico completo do projeto: o que foi construído, todas as versões e tentativas
              até aqui, os erros identificados em cada etapa, a solução final aplicada hoje e o
              teste ao vivo que confirma que agora{" "}
              <strong className="text-white">100% dos leads</strong> chegam ao RD Marketing e ao
              RD CRM com perguntas e respostas de qualificação.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Chip tone="info">RD Station Marketing</Chip>
              <Chip tone="info">RD Station CRM</Chip>
              <Chip tone="info">Meta Conversions API</Chip>
              <Chip tone="info">Server-side validation</Chip>
              <Chip tone="success">Auditoria em tempo real</Chip>
            </div>
          </div>

          {/* Objetivo */}
          <div className="grid gap-5 mb-5">
            <Card icon={Target} title="Objetivo do projeto" kicker="Por que isso existe" tone="info">
              <p>
                Centralizar e blindar todo o fluxo de captura de leads do portal Fisio em Ortopedia
                para que o time comercial receba leads completos, qualificados e rastreáveis — vindos
                de qualquer LP (aulas experimentais, ementas, pré-checkout, quizzes e pop-ups).
              </p>
              <ul className="space-y-2 mt-2">
                <Bullet tone="info">Lead entra no RD Marketing com nome, e-mail, telefone e tags corretas.</Bullet>
                <Bullet tone="info">Lead segue para o RD CRM no funil certo (UNIVERSITÁRIOS / FUNIL-POS) com perguntas e respostas nas anotações.</Bullet>
                <Bullet tone="info">Tráfego pago (Meta Ads / Google Ads) totalmente rastreado via Pixel + API de Conversões.</Bullet>
                <Bullet tone="info">Painel admin com log completo de cada tentativa de captura.</Bullet>
              </ul>
            </Card>
          </div>

          {/* Histórico / Timeline */}
          <h2 className="text-2xl font-bold text-white mt-10 mb-4 flex items-center gap-2">
            <History className="h-5 w-5 text-sky-300" /> Histórico de versões, tentativas e erros
          </h2>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 mb-2">
            <p className="text-sm text-white/70 mb-5">
              Cada linha abaixo representa uma versão real do fluxo de captura. As versões{" "}
              <Chip tone="info">azuis</Chip> foram iterações intermediárias — funcionavam parcialmente
              mas tinham um defeito que só ficou visível quando colocamos auditoria. A última,{" "}
              <Chip tone="success">verde</Chip>, é a solução de hoje.
            </p>
            <ol className="space-y-0">
              {TIMELINE.map((item, idx) => (
                <TimelineEntry key={item.version} item={item} last={idx === TIMELINE.length - 1} />
              ))}
            </ol>
          </div>

          {/* Etapas */}
          <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-2">
            <Workflow className="h-5 w-5 text-emerald-300" /> O que está em produção
          </h2>

          <div className="grid gap-5">
            <Card icon={Send} title="Capturas padronizadas" kicker="Frontend" tone="default">
              <ul className="space-y-2">
                <Bullet>LPs unificadas: aulas experimentais, ementa, pré-checkout, quizz-dor e quizz-pos.</Bullet>
                <Bullet>Formulários com campos obrigatórios: nome, e-mail e telefone (sem CREFITO).</Bullet>
                <Bullet>Pop-ups e quizzes só liberam o conteúdo após a captura completa.</Bullet>
              </ul>
            </Card>

            <Card icon={Tag} title="Tags & roteamento no CRM" kicker="Padronização" tone="default">
              <ul className="space-y-2">
                <Bullet>
                  <code className="text-white">aulas-exp-*</code>, <code className="text-white">ementa-site-*</code>,{" "}
                  <code className="text-white">pre-checkout-*</code>, <code className="text-white">quizz-dor</code>,{" "}
                  <code className="text-white">quizz-pos</code> + tag <code className="text-white">lead-*</code> de qualificação.
                </Bullet>
                <Bullet>
                  Roteamento RD CRM: <strong className="text-white">estudante</strong> → UNIVERSITÁRIOS;{" "}
                  <strong className="text-white">morno/quente</strong> → FUNIL-POS;{" "}
                  <strong className="text-white">frio não-estudante</strong> → não envia para o CRM.
                </Bullet>
                <Bullet>Pré-checkout sempre entra como morno (alta intenção de compra).</Bullet>
              </ul>
            </Card>

            <Card icon={ShieldCheck} title="Validação e qualidade do dado" kicker="Backend" tone="default">
              <ul className="space-y-2">
                <Bullet>Schema Zod compartilhado para todas as capturas — não aceita lead incompleto.</Bullet>
                <Bullet>Limpeza de leads de teste e de bots históricos da base.</Bullet>
                <Bullet>Builder de negócio em cascata: tenta os formatos do RD CRM até encaixar e sempre grava as anotações.</Bullet>
              </ul>
            </Card>

            <Card icon={Activity} title="Rastreamento e observabilidade" kicker="Tráfego pago" tone="default">
              <ul className="space-y-2">
                <Bullet>
                  Meta Pixel + Conversions API <b>migrados 100% para o Google Tag Manager</b> — o site
                  apenas dispara <code className="text-white">feo_lead_saved</code> /{" "}
                  <code className="text-white">feo_lead_qualified</code> no <code className="text-white">dataLayer</code>.
                  Detalhes em <Link to="/admin/plano-tracking-gtm" className="text-sky-300 underline-offset-2 hover:underline">/admin/plano-tracking-gtm</Link>.
                </Bullet>
                <Bullet>
                  Tabela <code className="text-white">lead_capture_attempts</code> registrando cada tentativa
                  (sucesso, falha, payload, endpoint, origem).
                </Bullet>
                <Bullet>
                  Painel em <Link to="/admin/leads" className="text-sky-300 underline-offset-2 hover:underline">/admin/leads</Link>{" "}
                  com filtros por período, endpoint, status e busca.
                </Bullet>
              </ul>
            </Card>
          </div>

          {/* Problema identificado HOJE */}
          <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-2">
            <Bug className="h-5 w-5 text-rose-300" /> Diagnóstico do problema principal
          </h2>

          <Card icon={AlertTriangle} title="Leads entravam no Meta Ads, mas não chegavam completos ao CRM" kicker="Causa raiz" tone="danger">
            <p>
              A campanha do Meta marcava centenas de leads e o RD Marketing recebia os contatos, mas
              o time comercial não enxergava os leads no <strong className="text-white">CRM</strong> com
              as <strong className="text-white">perguntas/respostas de qualificação</strong> nas
              anotações — exatamente a informação que o time usa para abordar o lead.
            </p>
            <p>Os três pontos críticos encontrados:</p>
            <ul className="space-y-2 mt-1">
              <Bullet tone="danger">
                <strong className="text-white">Campos customizados</strong> do RD Marketing não estavam
                no formato exato que a API exige — o payload era aceito mas as respostas não eram
                gravadas no contato.
              </Bullet>
              <Bullet tone="danger">
                <strong className="text-white">Negócio no CRM</strong> era criado sem as anotações porque
                o builder antigo não tinha fallback quando o RD recusava o formato do campo — falhava
                em silêncio.
              </Bullet>
              <Bullet tone="danger">
                Capturas vindas de fontes diferentes (pop-up, quizz, ementa) usavam{" "}
                <strong className="text-white">pipelines separados</strong>, então uma correção em uma
                LP não se propagava para as outras.
              </Bullet>
            </ul>
          </Card>

          {/* Solução */}
          <h2 className="text-2xl font-bold text-white mt-12 mb-4 flex items-center gap-2">
            <Wrench className="h-5 w-5 text-emerald-300" /> Como foi resolvido hoje
          </h2>

          <div className="grid gap-5">
            <Card icon={Database} title="Pipeline único e server-side" tone="success">
              <ul className="space-y-2">
                <Bullet tone="success">
                  Todas as LPs (atuais e futuras) passam pelo mesmo módulo de pós-processamento no
                  servidor (<code className="text-white">postprocess.server.ts</code>) — uma única
                  fonte de verdade para validar, taguear, enviar ao RD Marketing e abrir negócio no CRM.
                </Bullet>
                <Bullet tone="success">
                  Após o sucesso, o cliente empurra <code className="text-white">feo_lead_saved</code> /{" "}
                  <code className="text-white">feo_lead_qualified</code> no <code className="text-white">dataLayer</code>{" "}
                  e o GTM assume Meta Pixel + CAPI (dedup, hashing, cookies) — o site não envia mais nada direto ao Meta.
                </Bullet>
              </ul>
            </Card>

            <Card icon={ClipboardList} title="CRM com anotações garantidas" tone="success">
              <ul className="space-y-2">
                <Bullet tone="success">
                  Builder de negócio resiliente: tenta os formatos aceitos pela API do RD CRM em
                  cascata e sempre anexa perguntas e respostas como anotação estruturada.
                </Bullet>
                <Bullet tone="success">
                  Mapeamento dos campos customizados revalidado e travado em código — não depende mais
                  de configuração manual por LP.
                </Bullet>
                <Bullet tone="success">
                  Roteamento por estágio (universitário / morno / quente / frio) aplicado de forma
                  consistente em todas as fontes.
                </Bullet>
              </ul>
            </Card>

            <Card icon={Sparkles} title="Auditoria e monitoramento contínuo" tone="success">
              <ul className="space-y-2">
                <Bullet tone="success">
                  Cada tentativa de captura cai em <code className="text-white">lead_capture_attempts</code>{" "}
                  com payload e resultado — qualquer falha futura aparece em minutos.
                </Bullet>
                <Bullet tone="success">
                  Endpoints de teste protegidos por secret para validar a integração ponta-a-ponta
                  sem expor PII.
                </Bullet>
              </ul>
            </Card>
          </div>


          {/* Status final */}
          <div className="mt-12 rounded-3xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 via-white/[0.03] to-transparent p-7 sm:p-9">
            <div className="flex items-start gap-4">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-400/40 bg-emerald-500/15 shrink-0">
                <CheckCircle2 className="h-6 w-6 text-emerald-300" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-300 font-semibold">Status atual</div>
                <h3 className="mt-1 text-2xl font-bold text-white">Projeto concluído e monitorado</h3>
                <p className="mt-2 text-white/75 leading-relaxed">
                  As próximas LPs já nascem dentro do pipeline unificado — basta plugar o formulário
                  ao componente de captura padrão. O acompanhamento contínuo acontece pelo painel de
                  leads e pelo log de tentativas; qualquer regressão fica visível em minutos.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Chip tone="success">Capturas: OK</Chip>
                  <Chip tone="success">RD Marketing: OK</Chip>
                  <Chip tone="success">RD CRM (anotações): OK</Chip>
                  <Chip tone="success">Meta (via GTM): OK</Chip>
                  <Chip tone="success">Auditoria: ativa</Chip>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Link
                    to="/admin/leads"
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500/90 hover:bg-emerald-400 text-[#0b0a1f] px-4 py-2 text-sm font-semibold transition"
                  >
                    Ver leads & log de captura
                  </Link>
                  <Link
                    to="/admin/planos"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white px-4 py-2 text-sm font-semibold transition"
                  >
                    Voltar aos projetos
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
